import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, defer, Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { SpinnerService } from 'is-lib';
import { ServerResponse } from '../models/ServerResponse';
import { SnackBarService } from './snackbar.service';
import { SnackBarType } from '../models/Snackbar';
import { CACHE_EXPIRATION_SUBJECT, RequestCacheConfig } from '../models/Request';

type HeadersInput = HttpHeaders | { [header: string]: string | string[] };

interface RequestOptions {
	headers?: HeadersInput;
	[option: string]: any;
}

@Injectable({
	providedIn: 'root'
})
export class GenericService {
	public appInitialized: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(
		private _http: HttpClient,
		private _snackbar: SnackBarService,
		private _spinnerService: SpinnerService
	) {
	}

	/** Read on every request so a country change after login is picked up. */
	get selectedSIGA(): string | null {
		return localStorage.getItem('selectedSIGA');
	}

	//#region HTTP methods

	/**
	 * `errorConfig` says how a failed request is reported (the error is always rethrown to the caller): an ErrorConfig, or a boolean to just turn the snackbar on/off.
	 * Same for post, put, patch and delete. Defaults to showing the snackbar with the server's message (delete: no snackbar).
	 */
	get<T>(url: string, loaderConfig: LoaderConfig | boolean = false, params?: any, requestCacheConfig: RequestCacheConfig = new RequestCacheConfig(), errorConfig: ErrorConfig | boolean = new ErrorConfig()): Observable<ServerResponse<T>> {
		const headers = this.buildHeaders(undefined, requestCacheConfig, { 'Accept-Language': this.selectedSIGA ?? 'en-US' });

		return this.request(this._http.get<ServerResponse<T>>(url, { headers, params: params ?? undefined, context: this.buildContext(undefined, requestCacheConfig) }), loaderConfig, errorConfig);
	}

	post(url: string, body: any, options?: RequestOptions | HttpHeaders, loaderConfig: LoaderConfig | boolean = false, requestCacheConfig: RequestCacheConfig = new RequestCacheConfig(), errorConfig: ErrorConfig | boolean = new ErrorConfig()): Observable<any> {
		return this.request(this._http.post(url, body, this.buildOptions(options, requestCacheConfig)), loaderConfig, errorConfig);
	}

	put(url: string, body: any, options?: RequestOptions | HttpHeaders, loaderConfig: LoaderConfig | boolean = false, requestCacheConfig: RequestCacheConfig = new RequestCacheConfig(), errorConfig: ErrorConfig | boolean = new ErrorConfig()): Observable<any> {
		return this.request(this._http.put(url, body, this.buildOptions(options, requestCacheConfig)), loaderConfig, errorConfig);
	}

	patch(url: string, body: any, options?: RequestOptions | HttpHeaders, loaderConfig: LoaderConfig | boolean = false, requestCacheConfig: RequestCacheConfig = new RequestCacheConfig(), errorConfig: ErrorConfig | boolean = new ErrorConfig()): Observable<any> {
		return this.request(this._http.patch(url, body, this.buildOptions(options, requestCacheConfig)), loaderConfig, errorConfig);
	}

	delete<T>(url: string, loaderConfig: LoaderConfig | boolean = false, requestCacheConfig: RequestCacheConfig = new RequestCacheConfig(), errorConfig: ErrorConfig | boolean = new ErrorConfig(false)): Observable<ServerResponse<T>> {
		const headers = this.buildHeaders(undefined, requestCacheConfig);

		return this.request(this._http.delete<ServerResponse<T>>(url, { headers, context: this.buildContext(undefined, requestCacheConfig) }), loaderConfig, errorConfig);
	}

	//#endregion

	//#region Helpers

	/**
	 * Wraps a (cold) HttpClient observable so the loader is shown on subscribe and hidden on
	 * completion/error/unsubscribe. Reports errors as `errorConfig` says.
	 */
	private request<R>(request$: Observable<R>, loaderConfig: LoaderConfig | boolean, errorConfig: ErrorConfig | boolean): Observable<R> {
		const loader = LoaderConfig.from(loaderConfig);
		const errors = ErrorConfig.from(errorConfig);

		return defer(() => {
			this.showLoader(loader);
			return request$;
		}).pipe(
			catchError((err: HttpErrorResponse) => {
				const resolved = errors.resolve(err);

				if (resolved.showSnackbar) {
					this.showErrorSnackbar(err, resolved.message);
				}
				return throwError(() => err);
			}),
			finalize(() => this.hideLoader(loader))
		);
	}

	/** Accepts either an options object or a bare HttpHeaders (legacy call style) and adds the cache header. */
	private buildOptions(options: RequestOptions | HttpHeaders | undefined, requestCacheConfig: RequestCacheConfig): RequestOptions & { headers: HttpHeaders } {
		const normalized: RequestOptions = options instanceof HttpHeaders ? { headers: options } : { ...options };

		return { ...normalized, headers: this.buildHeaders(normalized.headers, requestCacheConfig), context: this.buildContext(normalized['context'], requestCacheConfig) };
	}

	private buildContext(context: HttpContext | undefined, requestCacheConfig: RequestCacheConfig): HttpContext {
		return (context ?? new HttpContext()).set(CACHE_EXPIRATION_SUBJECT, requestCacheConfig.expirationSubject$);
	}

	private buildHeaders(headers: HeadersInput | undefined, requestCacheConfig: RequestCacheConfig, extra: { [header: string]: string } = {}): HttpHeaders {
		let result = headers instanceof HttpHeaders ? headers : new HttpHeaders(headers ?? {});

		const toSet = { ...extra, 'ExpirationTimer': requestCacheConfig.persistenceTimerInMs.toString() };
		for (const [name, value] of Object.entries(toSet)) {
			result = result.set(name, value);
		}

		return result;
	}

	private showErrorSnackbar(err: HttpErrorResponse, customMessage?: string) {
		// A message given by the caller wins over the server's; without one the default below applies
		if (!String.isNullOrUndefinedOrWhitespace(customMessage)) {
			this._snackbar.callSnackBar(customMessage!, SnackBarType.ERROR);
			return;
		}

		const statusCode = err.error?.statusCode;
		const message = err.error?.message;

		this._snackbar.callSnackBar(`${statusCode} - ${message}`, SnackBarType.ERROR);
	}

	protected showLoader(loaderConfig: LoaderConfig) {
		if (!loaderConfig.show) {
			return;
		}

		if (String.isNullOrUndefinedOrWhitespace(loaderConfig.identifierName)) {
			this._spinnerService.showFullscreenSpinner();
		} else {
			this._spinnerService.showInComponentSpinner(loaderConfig.identifierName!);
		}
	}

	protected hideLoader(loaderConfig: LoaderConfig) {
		if (!loaderConfig.show) {
			return;
		}

		if (String.isNullOrUndefinedOrWhitespace(loaderConfig.identifierName)) {
			this._spinnerService.hideFullscreenSpinner();
		} else {
			this._spinnerService.hideInComponentSpinner(loaderConfig.identifierName!);
		}
	}

	//#endregion
}

export class LoaderConfig {
	show: boolean;
	fullscreen: boolean;
	identifierName?: string;

	constructor(show: boolean, fullscreen: boolean, identifierName?: string) {
		this.show = show;
		this.fullscreen = fullscreen;
		this.identifierName = identifierName;
	}

	static from(loaderConfig: LoaderConfig | boolean): LoaderConfig {
		return typeof loaderConfig === 'boolean' ? new LoaderConfig(loaderConfig, false) : loaderConfig;
	}
}

/** Overrides how a failed request is reported when the error matches `when`. */
export class ErrorRule {
	/** HTTP status code(s), or a predicate on the error, that this rule applies to */
	when: number | number[] | ((err: HttpErrorResponse) => boolean);
	/** Whether a snackbar is shown. Defaults to true */
	showSnackbar: boolean;
	/** i18n key or plain text for the snackbar. Without it the ErrorConfig's message, then the server's message, is used */
	message?: string;

	constructor(when: number | number[] | ((err: HttpErrorResponse) => boolean), showSnackbar: boolean = true, message?: string) {
		this.when = when;
		this.showSnackbar = showSnackbar;
		this.message = message;
	}

	matches(err: HttpErrorResponse): boolean {
		if (typeof this.when === 'function') {
			return this.when(err);
		}

		return Array.isArray(this.when) ? this.when.includes(err.status) : this.when === err.status;
	}
}

/**
 * How a failed request is reported. The error is always rethrown to the caller; this only controls the snackbar.
 * The first rule that matches the error wins; errors no rule matches use `showSnackbar` and `message`.
 * e.g. new ErrorConfig(true, undefined, [new ErrorRule(401, true, 'NO_CONTRACT_ACCESS'), new ErrorRule(404, false)])
 */
export class ErrorConfig {
	/** Whether a snackbar is shown when the request fails and no rule matches */
	showSnackbar: boolean;
	/** i18n key or plain text for the snackbar. Without it the server's message is shown (or a generic error if it sent none) */
	message?: string;
	rules: ErrorRule[];

	constructor(showSnackbar: boolean = true, message: string = 'INTERNAL_SERVER_ERROR', rules: ErrorRule[] = []) {
		this.showSnackbar = showSnackbar;
		this.message = message;
		this.rules = rules;
	}

	/** The snackbar settings that apply to this error */
	resolve(err: HttpErrorResponse): { showSnackbar: boolean, message?: string } {
		const rule = this.rules.find( (r) => r.matches(err) );

		return rule
			? { showSnackbar: rule.showSnackbar, message: rule.message ?? this.message }
			: { showSnackbar: this.showSnackbar, message: this.message };
	}

	static from(errorConfig: ErrorConfig | boolean): ErrorConfig {
		return typeof errorConfig === 'boolean' ? new ErrorConfig(errorConfig) : errorConfig;
	}
}
